"use client";

import { NodeIconTile } from "@/components/ui/node-icon-tile";
import type { ServerActionTriggerDef, ServerNodeStatistics } from "@/lib/api/server-fetchers";
import { cn } from "@/lib/utils";
import Link from "next/link";

import { useMemo, useState } from "react";

const PAGE_SIZE = 6;

interface IntegrationDetailActionsProps {
  triggers: ServerActionTriggerDef[];
  actions: ServerActionTriggerDef[];
  statistics?: Record<string, ServerNodeStatistics>;
}

type TabId = "all" | "triggers" | "actions";

export function IntegrationDetailActions({ triggers, actions, statistics }: IntegrationDetailActionsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const allItems = useMemo(() => {
    const items = [
      ...triggers.map(t => ({ ...t, integrationType: "trigger" as const })),
      ...actions.map(a => ({ ...a, integrationType: "action" as const })),
    ];

    if (statistics) {
      items.sort((a, b) => {
        const aRuns = statistics[a.type]?.totalExecutions || 0;
        const bRuns = statistics[b.type]?.totalExecutions || 0;
        return bRuns - aRuns;
      });
    }

    return items;
  }, [triggers, actions, statistics]);

  const filteredItems = useMemo(() => {
    if (activeTab === "triggers") return allItems.filter(i => i.integrationType === "trigger");
    if (activeTab === "actions") return allItems.filter(i => i.integrationType === "action");
    return allItems;
  }, [allItems, activeTab]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const remaining = filteredItems.length - visibleCount;

  const tabs: Array<{ id: TabId; label: string; count: number }> = [
    { id: "all", label: "All types", count: triggers.length + actions.length },
    { id: "triggers", label: "Triggers", count: triggers.length },
    { id: "actions", label: "Actions", count: actions.length },
  ];

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <h2 className="text-ds-text-primary leading-label text-2xl font-medium">Supported triggers and actions</h2>

        <div className="rounded-ds-sm border-ds-border bg-ds-surface-muted flex items-center gap-1 border p-1">
          {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  "rounded-ds-xs flex h-[36px] items-center gap-1.5 px-3 text-sm font-medium transition-all",
                  isActive ? "text-ds-text-secondary shadow-ds-elev-1 bg-ds-surface-card" : "text-ds-text-subtle",
                )}
              >
                {tab.label}
                <span className="rounded-ds-xs border-ds-border text-ds-text-secondary bg-ds-surface-card shadow-ds-soft flex h-[22px] min-w-[22px] items-center justify-center border px-1.5 text-xs font-medium">
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        {visibleItems.map(item => {
          const isTrigger = item.integrationType === "trigger";
          return (
            <Link
              key={`${item.integrationType}-${item.type}`}
              href={`/integrations/${item.integrationType}/${item.type}`}
              className="rounded-ds-lg border-ds-border bg-ds-surface-card shadow-ds-soft hover:bg-ds-surface-hover relative flex items-start gap-3 border p-3.5 transition-colors"
            >
              <div
                aria-hidden
                className="rounded-inherit shadow-ds-bottom-press pointer-events-none absolute inset-0"
              />

              <NodeIconTile
                size="md"
                id={item.type}
                subject={item.name}
                context={item.description}
                logoUrl={item.logoUrl}
                connectorType={item.connector?.type}
                innerClassName="bg-ds-surface-primary shadow-ds-b3-card"
              />

              <div className="flex min-w-0 flex-1 flex-col justify-center">
                <div className="flex w-full items-center justify-between gap-2">
                  <p className="text-ds-text-primary min-w-0 flex-1 truncate pt-1 text-base font-medium leading-6">
                    {item.name}
                  </p>
                  <span
                    className={cn(
                      "text-2xs leading-18 shrink-0 rounded-full border px-1.5 py-px font-medium",
                      isTrigger
                        ? "border-ds-warning-border bg-ds-warning-surface text-ds-warning-text"
                        : "border-ds-success-border bg-ds-success-surface text-ds-success-text",
                    )}
                  >
                    {isTrigger ? "Trigger" : "Action"}
                  </span>
                </div>
                <p className="text-ds-text-subtle mt-1 line-clamp-2 h-10 overflow-hidden text-sm leading-5">
                  {item.description}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      {remaining > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
            className="text-ds-focus-brand rounded-lg px-5 py-2 text-sm font-medium transition-colors hover:bg-black/5"
          >
            Load more ({remaining}) ↓
          </button>
        </div>
      )}
    </section>
  );
}
